import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Wallet, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { api } from '@/api/client';
import { WhatsappHistory, Card } from '@/features/whatsapp/WhatsappHistory';

interface MySummary {
  enabled: boolean; billingMode: string; walletBalance: number;
  pricePerMessage: number; gstPct: number; pricePerMessageInclGst: number;
  events: Array<{ event: string; label: string; enabled: boolean }>;
  thisMonth: { messages: number; charged: number };
  allTime: { messages: number; charged: number };
}
const rs = (n: number) => `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Merchant portal → Settings → SMS & WhatsApp. Read-only: Gaint switches WhatsApp on and sets the
 * price; the merchant sees whether it is on, for which events, what each message costs, how many
 * went out and what was charged — by AWB / LR — and can recharge a prepaid wallet.
 */
export function MerchantWhatsappPanel() {
  const [sub, setSub] = useState<'whatsapp' | 'sms'>('whatsapp');
  const { data, isLoading } = useQuery({ queryKey: ['whatsapp', 'my'], queryFn: async () => (await api.get<MySummary>('/whatsapp/my')).data });

  return (
    <div className="space-y-4" data-testid="merchant-wa">
      <div className="flex gap-1 border-b border-slate-200">
        {([['whatsapp', 'WhatsApp messages'], ['sms', 'SMS']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-3 py-2 text-sm -mb-px border-b-2 ${sub === id ? 'border-primary text-primary font-medium' : 'border-transparent text-slate-500'}`}>{label}</button>
        ))}
      </div>
      {sub === 'sms' && (
        <div className="text-sm text-slate-600 max-w-2xl space-y-2">
          <p>When a shipment is booked, your customer gets an SMS with its AWB (B2C) or LR (B2B) and the tracking link, sent by Gaint Logistic.</p>
          <p className="text-xs text-slate-400 flex items-center gap-1"><Lock size={11} /> Set by Gaint — contact your account manager to change.</p>
        </div>
      )}
      {sub === 'whatsapp' && (isLoading || !data ? (
        <div className="py-8 text-center"><Loader2 size={16} className="animate-spin inline text-slate-400" /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {data.enabled
              ? <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="mwa-status"><CheckCircle2 size={14} /> WhatsApp activated</span>
              : <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1" data-testid="mwa-status"><XCircle size={14} /> WhatsApp not activated</span>}
            <span className="text-xs text-slate-400 flex items-center gap-1"><Lock size={11} /> set by Gaint — contact your account manager to change</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Price per message" value={rs(data.pricePerMessageInclGst)} sub={`${rs(data.pricePerMessage)} + ${data.gstPct}% GST`} />
            <Card label="This month" value={`${data.thisMonth.messages} msg`} sub={`charged ${rs(data.thisMonth.charged)}`} />
            <Card label="All time" value={`${data.allTime.messages} msg`} sub={`charged ${rs(data.allTime.charged)}`} />
            <Card label={data.billingMode === 'POSTPAID' ? 'Billing' : 'Wallet balance'}
              value={data.billingMode === 'POSTPAID' ? 'Monthly invoice' : rs(data.walletBalance)}
              tone={data.billingMode !== 'POSTPAID' && data.walletBalance < data.pricePerMessageInclGst ? 'bad' : undefined}
              sub={data.billingMode === 'POSTPAID' ? 'a separate WhatsApp invoice each month' : 'messages are paid from this wallet'} />
          </div>

          {data.billingMode !== 'POSTPAID' && (
            <div className={`rounded-lg border p-3 flex flex-wrap items-center gap-3 text-sm ${data.walletBalance < data.pricePerMessageInclGst ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
              <Wallet size={16} />
              <span className="flex-1 min-w-[240px]">
                {data.walletBalance < data.pricePerMessageInclGst
                  ? 'Your wallet balance is too low — WhatsApp messages are not being sent. Recharge to resume them.'
                  : 'Each message is debited from your wallet when it is sent. A message WhatsApp could not deliver is refunded.'}
              </span>
              <Link to="/wallet" className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium" data-testid="mwa-recharge">Recharge wallet</Link>
            </div>
          )}

          <div className="border border-slate-200 rounded-lg">
            <div className="px-3 py-2 text-[11px] uppercase text-slate-500 bg-slate-50 border-b border-slate-200">Messages your customers get</div>
            <div className="flex flex-wrap gap-2 p-3">
              {data.events.map((e) => (
                <span key={e.event} className={`text-xs border rounded-full px-2.5 py-1 ${e.enabled && data.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                  {e.label}: {e.enabled && data.enabled ? 'on' : 'off'}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Message history</h3>
            <WhatsappHistory />
          </div>
        </>
      ))}
    </div>
  );
}
